import axiosInstance from '@api/axios';
import { DetailUseCase } from '@app/types/use-case';
import { saveBlobToDisk } from '@utils/utils.ts';
import { urls } from '@api/urls.ts';
import type { UseCase } from '@app/types/use-case';

// Placeholder API endpoints - will be replaced with real ones
export const updateUseCase = async (
  id: number | string,
  data: any
): Promise<DetailUseCase> => {
  const response = await axiosInstance.patch(
    urls.useCase.byId(Number(id)),
    data
  );
  return response.data;
};

export const generateUseCase = async (
  id: number,
  caseId?: number
): Promise<DetailUseCase> => {
  const payload = typeof caseId === 'number' ? { case_id: caseId } : {};
  const response = await axiosInstance.post(urls.useCase.generate(id), payload);
  return response.data;
};

export const getUseCase = async (id: number): Promise<DetailUseCase> => {
  const response = await axiosInstance.get(urls.useCase.byId(id));
  return response.data;
};

export const getUseCaseGenerationStatus = async (
  id: number
): Promise<{
  use_case_id: number;
  version_id: number | null;
  status: string | null;
}> => {
  const response = await axiosInstance.get(urls.useCase.generationStatus(id));
  return response.data;
};

export interface UseCaseProgressTask {
  task_type: string;
  task_name: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  progress_percentage: number;
  current_step: string | null;
  execution_time: number | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface UseCaseProgressResponse {
  document_id: number;
  version_id: number | null;
  overall_percentage: number;
  estimated_time_remaining: number;
  tasks: UseCaseProgressTask[];
}

export const getUseCaseProgress = async (
  id: number
): Promise<UseCaseProgressResponse> => {
  const response = await axiosInstance.get(urls.useCase.progress(id));
  return response.data;
};

export const listUseCases = async (params: {
  template?: number;
  case?: number;
}): Promise<UseCase[]> => {
  const query: Record<string, number> = {};
  if (typeof params.template === 'number') {
    query.template = params.template;
  }
  if (typeof params.case === 'number') {
    query.case = params.case;
  }
  const response = await axiosInstance.get(urls.useCase.base, {
    params: query,
  });
  return response.data?.results || response.data || [];
};

export const deleteUseCase = async (id: number): Promise<void> => {
  await axiosInstance.delete(urls.useCase.byId(id));
};

export const downloadUseCaseDocx = async (
  id: number,
  landscape?: boolean
): Promise<void> => {
  const response = await axiosInstance.get(urls.useCase.exportDocx(id), {
    responseType: 'blob',
    params: landscape ? { orientation: 'landscape' } : undefined,
  });

  saveBlobToDisk(response, `use_case_${id}.docx`);
};

export const cancelUseCase = async (id: number): Promise<void> => {
  const response = await axiosInstance.post(urls.useCase.cancelGeneration(id));

  return response.data;
};

export const generateUseCaseFromCase = async (
  templateId: number,
  caseId: number
): Promise<DetailUseCase> => {
  const response = await axiosInstance.post(urls.useCase.generateFromCase, {
    template_id: templateId,
    case_id: caseId,
  });
  return response.data;
};

export const linkCaseToUseCase = (
  useCaseId: number,
  caseId: number,
  callback: (data: any) => void,
  errorCallback?: (error: any) => void
) => {
  axiosInstance
    .post(urls.useCase.linkCase(useCaseId), { case_id: caseId })
    .then((response) => {
      callback(response.data);
    })
    .catch((error) => {
      if (errorCallback) {
        errorCallback(error);
      }
    });
};

export const createUseCaseDraft = async (data: {
  template_id: number;
  client_name: string;
  case_id?: number | null;
}): Promise<DetailUseCase> => {
  const form = new FormData();
  form.append('template_id', String(data.template_id));
  form.append('client_name', data.client_name);
  if (typeof data.case_id === 'number') {
    form.append('case_id', String(data.case_id));
  }
  const response = await axiosInstance.post(urls.useCase.saveDraft, form);
  return response.data;
};
